import React, { useState, useMemo, useEffect } from 'react';
import { 
  Search, 
  Plus, 
  Edit3, 
  Check, 
  X, 
  Download, 
  Upload, 
  ChevronDown, 
  ChevronUp,
  BellRing,
  Clock,
  CheckSquare,
  Trash2
} from 'lucide-react';
import { ManipulatedVariable, MVStatus, StepTest, getStepStartTimeMs } from '../types';
import { calculateReadinessScore, calculateDirectionalBalance } from '../services/readinessCalculator';
import { ExportService } from '../services/exportService';
import { EditMvModal } from './EditMvModal';

interface MvRegisterTableProps {
  mvs: ManipulatedVariable[];
  steps?: StepTest[];
  onUpdateMv: (mv: ManipulatedVariable, oldTag?: string) => void;
  onDeleteMv?: (mvId: string) => Promise<void> | void;
  onOpenStepModalForMv: (mv: ManipulatedVariable) => void;
  onOpenImportModal: () => void;
  selectedController: string;
  onSelectController: (controller: string) => void;
  projectName?: string;
  campaignName?: string;
  onAcknowledgeStep?: (step: StepTest, mv: ManipulatedVariable) => void;
  onCompleteHoldEarly?: (step: StepTest) => void;
  onDeleteStep?: (stepId: string) => Promise<void> | void;
  onUpdateStep?: (step: StepTest) => Promise<void> | void;
  now?: number;
  targetMovesPerMv?: number;
  highlightedMvId?: string | null;
}

export const MvRegisterTable: React.FC<MvRegisterTableProps> = ({
  mvs,
  steps = [] as StepTest[],
  onUpdateMv,
  onDeleteMv,
  onOpenStepModalForMv,
  onOpenImportModal,
  selectedController,
  onSelectController,
  projectName,
  campaignName,
  onAcknowledgeStep,
  onCompleteHoldEarly,
  onDeleteStep,
  onUpdateStep,
  now: externalNow,
  targetMovesPerMv = 20,
  highlightedMvId,
}) => {
  const [internalNow, setInternalNow] = useState<number>(() => Date.now());
  const now = externalNow ?? internalNow;

  // Tick every second if active steps exist and externalNow is not passed
  useEffect(() => {
    if (externalNow !== undefined) return;
    const hasActive = steps.some((s) => s.status === 'IN_PROGRESS' && !s.completedAt);
    if (!hasActive) return;
    const interval = setInterval(() => {
      setInternalNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [externalNow, steps]);
  const activeProjectName = projectName || campaignName || 'APC Project';
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [readinessFilter, setReadinessFilter] = useState<string>('ALL');
  const [editingMv, setEditingMv] = useState<ManipulatedVariable | null>(null);

  // If an external jump targets a variable, clear local filters so it's guaranteed visible
  useEffect(() => {
    if (highlightedMvId) {
      setSearchTerm('');
      setStatusFilter('ALL');
      setReadinessFilter('ALL');
    }
  }, [highlightedMvId]);

  // Clock ticker for step settling hold duration (updates every 5 seconds)
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  // Dynamically derive unique subcontrollers from MVs in the table
  const subcontrollers = useMemo(() => {
    const set = new Set<string>();
    mvs.forEach((m) => {
      const c = (m.controller || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [mvs]);

  const activeStepsByMv = useMemo(() => {
    const map = new Map<string, StepTest>();
    // Sort steps newest first
    const sorted = [...steps].sort((a, b) => {
      const timeA = new Date((a.createdAt || a.timestamp).replace(' ', 'T')).getTime();
      const timeB = new Date((b.createdAt || b.timestamp).replace(' ', 'T')).getTime();
      return timeB - timeA;
    });

    // Only consider the most recent step per MV
    const seenMvs = new Set<string>();
    for (const s of sorted) {
      const keyId = s.mvId;
      const keyTag = s.mvTag;
      if (!seenMvs.has(keyId) && !seenMvs.has(keyTag)) {
        seenMvs.add(keyId);
        seenMvs.add(keyTag);
        if (s.status === 'IN_PROGRESS') {
          map.set(keyId, s);
          map.set(keyTag, s);
        }
      }
    }
    return map;
  }, [steps]);

  // Sorting
  const [sortField, setSortField] = useState<keyof ManipulatedVariable>('tag');
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  const handleSort = (field: keyof ManipulatedVariable) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Filtered and Sorted MVs
  const filteredMvs = useMemo(() => {
    return mvs.filter((mv) => {
      if (selectedController !== 'ALL' && mv.controller !== selectedController) {
        return false;
      }
      if (statusFilter !== 'ALL') {
        if (statusFilter === 'Step Complete') {
          if (mv.status !== 'Step Complete' && mv.status !== 'Complete') return false;
        } else if (mv.status !== statusFilter) {
          return false;
        }
      }
      if (readinessFilter !== 'ALL') {
        const score = calculateReadinessScore(mv.validMoves);
        if (score !== readinessFilter) return false;
      }
      if (searchTerm.trim() !== '') {
        const query = searchTerm.toLowerCase();
        const matchTag = mv.tag.toLowerCase().includes(query);
        const matchDesc = mv.description.toLowerCase().includes(query);
        const matchNotes = (mv.notes || '').toLowerCase().includes(query);
        const matchCtrl = mv.controller.toLowerCase().includes(query);
        const matchArea = (mv.processArea || '').toLowerCase().includes(query);
        return matchTag || matchDesc || matchNotes || matchCtrl || matchArea;
      }
      return true;
    }).sort((a, b) => {
      let valA = a[sortField] ?? '';
      let valB = b[sortField] ?? '';
      if (typeof valA === 'string' || typeof valB === 'string') {
        return sortAsc 
          ? String(valA).localeCompare(String(valB)) 
          : String(valB).localeCompare(String(valA));
      }
      return sortAsc ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
    });
  }, [mvs, selectedController, statusFilter, readinessFilter, searchTerm, sortField, sortAsc]);

  const getStatusBadge = (status: MVStatus) => {
    switch (status) {
      case 'Model Ready':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Step Complete':
      case 'Complete':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Active':
        return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'Waiting for Steady State':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Rejected':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'Not Started':
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getReadinessBadge = (validMoves: number, target: number = targetMovesPerMv) => {
    const score = calculateReadinessScore(validMoves, target);
    switch (score) {
      case 'Ready for Modeling':
        return <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Ready</span>;
      case 'Adequate':
        return <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">Adequate</span>;
      case 'Insufficient':
      default:
        return <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200">Low</span>;
    }
  };

  const completedWaitingCount = useMemo(() => {
    let count = 0;
    const seen = new Set<string>();
    activeStepsByMv.forEach((step) => {
      if (!seen.has(step.id)) {
        seen.add(step.id);
        const startMs = getStepStartTimeMs(step);
        const durationMinutes = step.holdDuration || 60;
        const durationMs = durationMinutes * 60 * 1000;
        const endMs = startMs + durationMs;
        if (step.completedAt || now >= endMs) {
          count++;
        }
      }
    });
    return count;
  }, [activeStepsByMv, now]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      {/* Control Bar: Subcontroller tabs + Search + Filters */}
      <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white">
        {/* Subcontroller Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
          {['ALL', ...subcontrollers].map((tab) => {
            const count = tab === 'ALL' ? mvs.length : mvs.filter((m) => m.controller === tab).length;
            const active = selectedController === tab;
            return (
              <button
                key={tab}
                onClick={() => onSelectController(tab)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70 hover:text-slate-900'
                }`}
              >
                {tab === 'ALL' ? 'All MVs' : tab} ({count})
              </button>
            );
          })}
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search Input */}
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search tag, desc..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-xs bg-slate-50 text-slate-800 pl-8 pr-3 py-1.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 w-full sm:w-48"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Status Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs bg-slate-50 text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
          >
            <option value="ALL">All Statuses</option>
            <option value="Model Ready">Model Ready</option>
            <option value="Step Complete">Step Complete</option>
            <option value="Active">Active</option>
            <option value="Waiting for Steady State">Waiting for Steady State</option>
            <option value="Rejected">Rejected</option>
            <option value="Not Started">Not Started</option>
          </select>

          {/* Readiness Dropdown */}
          <select
            value={readinessFilter}
            onChange={(e) => setReadinessFilter(e.target.value)}
            className="text-xs bg-slate-50 text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 hidden sm:inline-block"
          >
            <option value="ALL">All Readiness</option>
            <option value="Ready for Modeling">Ready (20+)</option>
            <option value="Adequate">Adequate (10-19)</option>
            <option value="Insufficient">Low (&lt;10)</option>
          </select>

          {/* Excel Template Button */}
          <button
            onClick={() => ExportService.downloadMvTemplateExcel()}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-xs transition-colors"
            title="Download blank Microsoft Excel template (.xlsx) with sample variables"
          >
            <Download className="w-3 h-3 text-emerald-600" />
            <span>Template (.xlsx)</span>
          </button>

          {/* Import Excel / CSV Button */}
          <button
            onClick={onOpenImportModal}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 shadow-xs transition-colors"
            title="Import variables from completed Excel or CSV file"
          >
            <Upload className="w-3 h-3 text-emerald-600" />
            <span>Import Excel</span>
          </button>

          {/* Add Variable Button */}
          <button
            onClick={() => {
              setEditingMv({
                id: '',
                campaignId: '',
                tag: '',
                description: '',
                controller: subcontrollers[0] || '',
                processArea: '',
                units: '%',
                recommendedStepSize: 5.0,
                approvedStepSize: 5.0,
                status: 'Not Started',
                positiveMoves: 0,
                negativeMoves: 0,
                validMoves: 0,
                rejectedMoves: 0,
                notes: '',
                updatedAt: new Date().toISOString(),
              });
            }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-xs transition-colors cursor-pointer"
            title="Create a new Manipulated Variable with a new or existing subcontroller"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>+ MV</span>
          </button>
        </div>
      </div>

      {/* Completion Notification Banner */}
      {completedWaitingCount > 0 && (
        <div className="mx-3 sm:mx-4 my-2.5 p-3 bg-amber-50 border-2 border-amber-400 rounded-xl flex items-center justify-between gap-3 animate-step-blink">
          <div className="flex items-center gap-2.5">
            <BellRing className="w-5 h-5 text-amber-600 animate-bounce shrink-0" />
            <div>
              <div className="font-bold text-amber-950 text-xs sm:text-sm">
                {completedWaitingCount} Step Test{completedWaitingCount > 1 ? 's' : ''} Completed Settling Duration!
              </div>
              <div className="text-[11px] text-amber-800">
                Variable row is blinking below. Click <strong>Acknowledge & Evaluate</strong> to review steady state and declare PASS / FAIL.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Responsive Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 uppercase font-mono text-[11px] tracking-wider">
              {/* Action column at front */}
              <th className="py-3 px-3 text-left whitespace-nowrap">Action</th>

              {/* 1. Subcontroller */}
              <th 
                className="py-3 px-3 cursor-pointer hover:text-slate-900"
                onClick={() => handleSort('controller')}
              >
                <div className="flex items-center gap-1">
                  <span>Subcontroller</span>
                  {sortField === 'controller' && (sortAsc ? <ChevronUp className="w-3 h-3 text-sky-600" /> : <ChevronDown className="w-3 h-3 text-sky-600" />)}
                </div>
              </th>

              {/* 2. Process Area */}
              <th 
                className="py-3 px-3 cursor-pointer hover:text-slate-900"
                onClick={() => handleSort('processArea')}
              >
                <div className="flex items-center gap-1">
                  <span>Process Area</span>
                  {sortField === 'processArea' && (sortAsc ? <ChevronUp className="w-3 h-3 text-sky-600" /> : <ChevronDown className="w-3 h-3 text-sky-600" />)}
                </div>
              </th>

              {/* 3. Tag */}
              <th 
                className="py-3 px-3 sm:px-4 cursor-pointer hover:text-slate-900"
                onClick={() => handleSort('tag')}
              >
                <div className="flex items-center gap-1">
                  <span>Tag</span>
                  {sortField === 'tag' && (sortAsc ? <ChevronUp className="w-3 h-3 text-sky-600" /> : <ChevronDown className="w-3 h-3 text-sky-600" />)}
                </div>
              </th>

              {/* 4. Description */}
              <th className="py-3 px-3 min-w-[180px]">Description</th>

              {/* 5. Units */}
              <th className="py-3 px-2">Units</th>

              {/* 6. Recommended Step */}
              <th className="py-3 px-2 text-right">Recommended Step</th>

              {/* 7. Approved Step */}
              <th className="py-3 px-2 text-right">Approved Step</th>

              <th className="py-3 px-3 text-center">Dir (+/-)</th>
              <th 
                className="py-3 px-3 cursor-pointer hover:text-slate-900 text-center"
                onClick={() => handleSort('validMoves')}
              >
                <div className="flex items-center gap-1 justify-center">
                  <span>Valid / {targetMovesPerMv}</span>
                  {sortField === 'validMoves' && (sortAsc ? <ChevronUp className="w-3 h-3 text-sky-600" /> : <ChevronDown className="w-3 h-3 text-sky-600" />)}
                </div>
              </th>
              <th className="py-3 px-2 text-center">Rej</th>
              <th className="py-3 px-3 text-center">Status</th>
              <th className="py-3 px-3 text-center">Readiness</th>
              <th className="py-3 px-3 min-w-[140px]">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-sans">
            {filteredMvs.length === 0 ? (
              <tr>
                <td colSpan={14} className="py-10 text-center text-slate-400">
                  No manipulated variables match your filter.
                </td>
              </tr>
            ) : (
              filteredMvs.map((mv) => {
                const balance = calculateDirectionalBalance(mv.positiveMoves, mv.negativeMoves);
                const mvTarget = mv.targetMoves || targetMovesPerMv;
                const progressPct = Math.min(100, Math.round((mv.validMoves / mvTarget) * 100));

                const activeStep = activeStepsByMv.get(mv.id) || activeStepsByMv.get(mv.tag);
                let isStepCompleted = false;
                let remainingMinutes = 0;
                let remainingSeconds = 0;
                if (activeStep) {
                  if (activeStep.completedAt) {
                    isStepCompleted = true;
                  } else {
                    const startMs = getStepStartTimeMs(activeStep);
                    const durationMinutes = activeStep.holdDuration || 60;
                    const durationMs = durationMinutes * 60 * 1000;
                    const endMs = startMs + durationMs;
                    if (now >= endMs) {
                      isStepCompleted = true;
                    } else {
                      const remainingMs = endMs - now;
                      remainingMinutes = Math.floor(remainingMs / 60000);
                      remainingSeconds = Math.floor((remainingMs % 60000) / 1000);
                    }
                  }
                }

                const isHighlighted = !!highlightedMvId && (
                  mv.id === highlightedMvId || 
                  (mv.tag && highlightedMvId.toUpperCase() === mv.tag.toUpperCase())
                );

                return (
                  <tr 
                    key={mv.id} 
                    id={`mv-row-${mv.id}`}
                    data-mv-tag={mv.tag}
                    data-mv-id={mv.id}
                    className={`transition-all cursor-pointer group ${
                      isHighlighted
                        ? 'animate-row-highlight z-20 relative font-medium'
                        : isStepCompleted
                        ? 'animate-step-blink border-y-2 border-amber-500'
                        : activeStep
                        ? 'bg-sky-50/60 hover:bg-sky-100/60'
                        : 'hover:bg-slate-50/90'
                    }`}
                    title={
                      isStepCompleted 
                        ? 'Hold duration completed! Click to acknowledge & evaluate' 
                        : 'Click row to view and edit variable details'
                    }
                    onClick={() => {
                      if (isStepCompleted && activeStep) {
                        onAcknowledgeStep?.(activeStep, mv);
                      } else {
                        setEditingMv(mv);
                      }
                    }}
                  >
                    {/* Action Column at front */}
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      {isStepCompleted && activeStep ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAcknowledgeStep?.(activeStep, mv);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-md transition-all flex items-center gap-1.5 hover:scale-105 cursor-pointer"
                            title="Step settling complete! Click to evaluate Pass/Fail"
                          >
                            <CheckSquare className="w-3.5 h-3.5" />
                            <span>Acknowledge & Evaluate</span>
                          </button>
                        </div>
                      ) : activeStep ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCompleteHoldEarly?.(activeStep);
                            }}
                            className="px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 text-xs font-medium transition-colors flex items-center gap-1 shadow-xs cursor-pointer"
                            title="Settling achieved: Complete hold duration now and trigger evaluation"
                          >
                            <Check className="w-3 h-3 text-amber-600" />
                            <span>Finish Hold</span>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenStepModalForMv(mv);
                            }}
                            className="px-2.5 py-1 rounded-md bg-sky-50 hover:bg-sky-600 text-sky-700 hover:text-white border border-sky-200 text-xs font-medium transition-colors flex items-center gap-1 shadow-xs cursor-pointer"
                            title="Record a step test"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Step</span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingMv(mv);
                            }}
                            className="p-1 rounded-md text-slate-400 hover:text-sky-600 hover:bg-sky-50 transition-colors cursor-pointer"
                            title="Edit variable details or tag name"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          {onDeleteMv && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Are you sure you want to delete variable ${mv.tag}? Any recorded step tests will also be removed.`)) {
                                  onDeleteMv(mv.id);
                                }
                              }}
                              className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                              title="Delete variable from project"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                    {/* 1. Subcontroller */}
                    <td className="py-2.5 px-3 text-slate-700 whitespace-nowrap">
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-medium text-slate-700 border border-slate-200">
                        {mv.controller}
                      </span>
                    </td>

                    {/* 2. Process Area */}
                    <td className="py-2.5 px-3 text-slate-600 text-xs whitespace-nowrap">
                      {mv.processArea || '-'}
                    </td>

                    {/* 3. Tag */}
                    <td className="py-2.5 px-3 sm:px-4 font-mono font-bold text-sky-700 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="group-hover:underline">{mv.tag}</span>
                        {isHighlighted && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-sky-600 text-white shadow-xs animate-pulse">
                            Selected
                          </span>
                        )}
                        {isStepCompleted && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-600 text-white animate-pulse shadow-xs">
                            <BellRing className="w-3 h-3" /> Ready for Eval
                          </span>
                        )}
                        {activeStep && !isStepCompleted && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-sky-100 text-sky-800 border border-sky-300">
                            <Clock className="w-3 h-3 text-sky-600 animate-spin" /> {remainingMinutes > 60 ? `${(remainingMinutes/60).toFixed(1)}h` : `${remainingMinutes}m`} left
                          </span>
                        )}
                      </div>
                    </td>

                    {/* 4. Description */}
                    <td className="py-2.5 px-3 text-slate-800 font-medium max-w-xs truncate" title={mv.description}>
                      {mv.description}
                    </td>

                    {/* 5. Units */}
                    <td className="py-2.5 px-2 font-mono text-slate-500 text-[11px] whitespace-nowrap">
                      {mv.units}
                    </td>

                    {/* 6. Recommended Step */}
                    <td className="py-2.5 px-2 text-right font-mono text-slate-600 text-xs">
                      {mv.recommendedStepSize}
                    </td>

                    {/* 7. Approved Step */}
                    <td className="py-2.5 px-2 text-right font-mono">
                      <span className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors">
                        {mv.approvedStepSize}
                      </span>
                    </td>

                    {/* Directional Balance (+ / -) */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      <div className="inline-flex items-center gap-1 font-mono text-[11px]">
                        <span className="text-emerald-700 font-semibold">+{mv.positiveMoves}</span>
                        <span className="text-slate-300">/</span>
                        <span className="text-rose-600 font-semibold">-{mv.negativeMoves}</span>
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {balance.skew}
                      </div>
                    </td>

                    {/* Valid Moves Progress */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      <div className="font-mono text-xs font-semibold text-slate-900">
                        {mv.validMoves} <span className="text-slate-400 font-normal text-[10px]">/{mvTarget}</span>
                      </div>
                      <div className="w-16 bg-slate-100 h-1.5 rounded-full mx-auto mt-1 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            progressPct >= 100
                              ? 'bg-emerald-600'
                              : progressPct >= 50
                              ? 'bg-sky-600'
                              : 'bg-slate-400'
                          }`}
                          style={{ width: `${progressPct}%` }}
                        ></div>
                      </div>
                    </td>

                    {/* Rejected Moves */}
                    <td className="py-2.5 px-2 text-center font-mono">
                      <span className={mv.rejectedMoves > 0 ? 'text-rose-600 font-semibold' : 'text-slate-300'}>
                        {mv.rejectedMoves}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      {isStepCompleted && activeStep ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAcknowledgeStep?.(activeStep, mv);
                          }}
                          className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500 hover:bg-amber-600 text-white shadow-xs animate-bounce flex items-center justify-center gap-1 mx-auto cursor-pointer"
                          title="Click to acknowledge & evaluate"
                        >
                          <CheckSquare className="w-3.5 h-3.5" />
                          <span>Acknowledge</span>
                        </button>
                      ) : activeStep ? (
                        <span 
                          className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800 border border-sky-300 flex items-center justify-center gap-1 mx-auto w-fit"
                          title={`${remainingMinutes}m ${remainingSeconds}s remaining`}
                        >
                          <span className="w-2 h-2 rounded-full bg-sky-600 animate-ping"></span> Active ({remainingMinutes > 0 ? `${remainingMinutes}m` : `${remainingSeconds}s`})
                        </span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${getStatusBadge(mv.status)}`}>
                          {mv.status === 'Complete' ? 'Step Complete' : mv.status}
                        </span>
                      )}
                    </td>

                    {/* Readiness */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      {getReadinessBadge(mv.validMoves, mvTarget)}
                    </td>

                    {/* Notes */}
                    <td className="py-2.5 px-3 text-slate-500 text-xs max-w-xs">
                      <span className="truncate block" title={mv.notes}>
                        {mv.notes || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-500 gap-1">
        <span>Showing {filteredMvs.length} of {mvs.length} Manipulated Variables</span>
        <span>Goal: {targetMovesPerMv} Valid moves per MV for controller model identification</span>
      </div>

      {/* Edit MV Dialog Modal */}
      <EditMvModal
        isOpen={!!editingMv}
        mv={editingMv ? (mvs.find((m) => m.id === editingMv.id) || editingMv) : null}
        steps={steps}
        existingSubcontrollers={subcontrollers}
        isNew={editingMv ? !editingMv.id : false}
        onClose={() => setEditingMv(null)}
        onSave={onUpdateMv}
        onDeleteMv={onDeleteMv}
        onDeleteStep={onDeleteStep}
        onUpdateStep={onUpdateStep}
        targetMoves={targetMovesPerMv}
      />
    </div>
  );
};
